/**
 * Strixly Icon-Bibliothek
 * Alle Icons kommen aus @tabler/icons-react (MIT-Lizenz, kommerziell nutzbar).
 *
 * Verwendung:
 *   import { IconSearch, IconPlus } from '../components/icons';
 *   <IconSearch />                      → Default: size=18, stroke=2
 *   <IconSearch size={24} />            → Override
 *   <IconSearch className="my-class" /> → CSS-Klasse
 *
 * Icons hier ergänzen wenn neue benötigt werden.
 * Tabler-Katalog: https://tabler.io/icons
 */

import React from 'react';
import {
  IconLayoutGrid     as TGrid,
  IconCalendar       as TCalendar,
  IconSearch         as TSearch,
  IconCamera         as TCamera,
  IconPlus           as TPlus,
  IconShoppingBag    as TShopping,
  IconSettings       as TSettings,
  IconLogout         as TLogout,
  IconShieldHalf     as TShield,
  IconFileText       as TFile,
  IconDownload       as TDownload,
  IconBook           as TBook,
  IconPencil         as TPencil,
  IconTrash          as TTrash,
  IconHeart          as THeart,
  IconStar           as TStar,
  IconBell           as TBell,
  IconClipboard      as TClipboard,
  IconClipboardCheck as TClipboardCheck,
  IconShare          as TShare,
  IconLink           as TLink,
  IconEye            as TEye,
  IconEyeOff         as TEyeOff,
  IconKey            as TKey,
  IconCheck          as TCheck,
  IconX              as TX,
  IconArrowLeft      as TArrowLeft,
  IconArrowRight     as TArrowRight,
  IconChevronUp      as TChevUp,
  IconChevronDown    as TChevDown,
  IconChevronRight   as TChevRight,
  IconAlertTriangle  as TWarning,
  IconInfoCircle     as TInfo,
  IconCircleCheck    as TCircleCheck,
  IconCircleX        as TCircleX,
  IconCirclePlus     as TCirclePlus,
  IconLoader2        as TLoader,
  IconClock          as TClock,
  IconUsers          as TUsers,
  IconUser           as TUser,
  IconSparkles       as TSparkles,
  IconMail           as TMail,
  IconMessage        as TMessage,
  IconTool           as TTool,
  IconRefresh        as TRefresh,
  IconScan           as TScan,
  IconChefHat        as TChefHat,
  IconToolsKitchen2  as TKitchen,
  IconFridge         as TFridge,
  IconPhoto          as TPhoto,
  IconUpload         as TUpload,
  IconWorldWww       as TWorld,
  IconFlag           as TFlag,
  IconBug            as TBug,
  IconBulb           as TBulb,
  IconLock           as TLock,
  IconLockOpen       as TUnlock,
  IconQrcode         as TQr,
  IconMicroscope     as TLab,
  IconLeaf           as TLeaf,
  IconFilter         as TFilter,
  IconSortAscending  as TSort,
  IconDotsVertical   as TMore,
  IconExternalLink   as TExternal,
  IconCopy           as TCopy,
  IconHome           as THome,
  IconAward          as TAward,
  IconBrandWhatsapp  as TWhatsapp,
  IconBrandFacebook  as TFacebook,
  IconBrandX         as TBrandX,
  IconBrandTelegram  as TTelegram,
  IconBrandLinkedin  as TLinkedin,
  IconWand           as TWand,
} from '@tabler/icons-react';

// Standard-Props für alle Icons
const DEFAULTS = { size: 18, stroke: 2 };

function wrap(TablerIcon) {
  return function Icon({ size, stroke, className, style, ...rest }) {
    return (
      <TablerIcon
        size={size ?? DEFAULTS.size}
        stroke={stroke ?? DEFAULTS.stroke}
        className={className}
        style={style}
        aria-hidden="true"
        {...rest}
      />
    );
  };
}

// ── Navigation & Layout ──────────────────────────────────────────────────────
export const IconNavGrid       = wrap(TGrid);        // Dashboard / Startseite
export const IconNavCalendar   = wrap(TCalendar);    // Wochenplaner
export const IconNavFridge     = wrap(TFridge);      // Kühlschrank
export const IconNavBook       = wrap(TBook);        // Meine Rezepte
export const IconNavSearch     = wrap(TSearch);      // Alle Rezepte / Suche
export const IconNavShopping   = wrap(TShopping);    // Einkaufsliste
export const IconNavSettings   = wrap(TSettings);    // Einstellungen
export const IconNavLogout     = wrap(TLogout);      // Abmelden
export const IconNavAdmin      = wrap(TShield);      // Administration
export const IconNavCamera     = wrap(TCamera);      // Kühlschrank scannen
export const IconNavFile       = wrap(TFile);        // Rezept scannen
export const IconNavImport     = wrap(TDownload);    // Externer Rezept-Import
export const IconNavPlus       = wrap(TPlus);        // Manuelle Eingabe
export const IconNavHome       = wrap(THome);        // Zurück-Link

// ── Aktions-Buttons ──────────────────────────────────────────────────────────
export const IconEdit          = wrap(TPencil);      // Bearbeiten
export const IconDelete        = wrap(TTrash);       // Löschen
export const IconFavorite      = wrap(THeart);       // Favorit
export const IconRating        = wrap(TStar);        // Bewertung
export const IconCopyText      = wrap(TCopy);        // Text kopieren
export const IconClipboardCopy = wrap(TClipboard);   // In Zwischenablage
export const IconClipboardDone = wrap(TClipboardCheck); // Kopiert ✓
export const IconShareAction   = wrap(TShare);       // Teilen
export const IconLinkAction    = wrap(TLink);        // URL / Link
export const IconEyeShow       = wrap(TEye);         // Passwort anzeigen
export const IconEyeHide       = wrap(TEyeOff);      // Passwort verstecken
export const IconKeyAction     = wrap(TKey);         // Passwort / 2FA
export const IconQrAction      = wrap(TQr);          // QR-Code / 2FA
export const IconRefreshAction = wrap(TRefresh);     // Neu laden / Wiederholen
export const IconUploadAction  = wrap(TUpload);      // Hochladen
export const IconExternalLink  = wrap(TExternal);    // Extern öffnen
export const IconMore          = wrap(TMore);        // Mehr-Menü
export const IconFilter        = wrap(TFilter);      // Filtern
export const IconSort          = wrap(TSort);        // Sortieren

// ── Status & Feedback ────────────────────────────────────────────────────────
export const IconCheck         = wrap(TCheck);       // Erledigt ✓
export const IconClose         = wrap(TX);           // Schließen ✕
export const IconWarning       = wrap(TWarning);     // Warnung ⚠️
export const IconInfo          = wrap(TInfo);        // Info
export const IconSuccess       = wrap(TCircleCheck); // Erfolg
export const IconError         = wrap(TCircleX);     // Fehler
export const IconAdd           = wrap(TCirclePlus);  // Hinzufügen
export const IconLoader        = wrap(TLoader);      // Laden (animierbar)
export const IconFlag          = wrap(TFlag);        // Melden
export const IconBug           = wrap(TBug);         // Bug melden
export const IconIdea          = wrap(TBulb);        // Idee schicken
export const IconAward         = wrap(TAward);       // Badge / Auszeichnung

// ── Navigation Pfeile ────────────────────────────────────────────────────────
export const IconBack          = wrap(TArrowLeft);   // ← Zurück
export const IconForward       = wrap(TArrowRight);  // → Weiter
export const IconChevUp        = wrap(TChevUp);      // ▲ aufklappen
export const IconChevDown      = wrap(TChevDown);    // ▼ zuklappen
export const IconChevRight     = wrap(TChevRight);   // › nächste Seite

// ── Inhalts-Icons ────────────────────────────────────────────────────────────
export const IconClock         = wrap(TClock);       // Zubereitungszeit
export const IconUsers         = wrap(TUsers);       // Portionen
export const IconUser          = wrap(TUser);        // Profil
export const IconSparkles      = wrap(TSparkles);    // Funken / AI-Effekte
export const IconWand          = wrap(TWand);        // KI-Rezept / AI-Badge
export const IconMail          = wrap(TMail);        // E-Mail / Nachrichten
export const IconMessage       = wrap(TMessage);     // Chat / Benachrichtigung
export const IconBell          = wrap(TBell);        // Benachrichtigungen
export const IconPhoto         = wrap(TPhoto);       // Bild / Foto
export const IconScan          = wrap(TScan);        // Scan
export const IconChefHat       = wrap(TChefHat);     // Kochen / Rezept
export const IconRecipe        = wrap(TKitchen);     // Küche / Rezept
export const IconWorld         = wrap(TWorld);       // Website / URL-Import
export const IconLock          = wrap(TLock);        // Privat / gesperrt
export const IconUnlock        = wrap(TUnlock);      // Öffentlich
export const IconLeaf          = wrap(TLeaf);        // Vegan / Allergen
export const IconLab           = wrap(TLab);         // KI-Analyse
export const IconTool          = wrap(TTool);        // Test / Debug

// ── Social-Media-Brand-Icons (für Share-Modal) ───────────────────────────────
export const IconBrandWhatsapp = wrap(TWhatsapp);    // WhatsApp
export const IconBrandFacebook = wrap(TFacebook);    // Facebook
export const IconBrandX        = wrap(TBrandX);      // X (Twitter)
export const IconBrandTelegram = wrap(TTelegram);    // Telegram
export const IconBrandLinkedin = wrap(TLinkedin);    // LinkedIn
